import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Search, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

const QUARTILES = ["All", "Q1", "Q2", "Q3", "Q4"];
const DOMAINS = ["All", "Computer Science", "AI/ML", "Public Health", "Climate Science",
  "Environment", "Economics", "Education", "Civil Engineering", "Multidisciplinary"];

const quartileBadge = {
  Q1: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Q2: "bg-sky-50 text-sky-700 border-sky-200",
  Q3: "bg-amber-50 text-amber-700 border-amber-200",
  Q4: "bg-slate-100 text-slate-700 border-slate-200",
};

const QUARTILE_ORDER = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 };

function SortableHeader({ label, sortKey, sort, onSort, className = "" }) {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      onClick={() => onSort(sortKey)}
      className={`inline-flex items-center gap-1 hover:text-kb-purple transition-colors text-left w-full ${active ? "text-kb-purple" : ""} ${className}`}
      data-testid={`journals-sort-${sortKey}`}
    >
      <span>{label}</span>
      <Icon className="w-3 h-3" strokeWidth={2} />
    </button>
  );
}

export default function Journals() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [quartile, setQuartile] = useState("All");
  const [domain, setDomain] = useState("All");
  const [maxApc, setMaxApc] = useState("");
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: "name", dir: "asc" });

  const onSort = (key) => {
    setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  };

  const fetchAll = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (quartile !== "All") params.quartile = quartile;
    if (domain !== "All") params.domain = domain;
    if (maxApc) params.max_apc = Number(maxApc);
    const r = await api.get("/journals", { params });
    setItems(r.data);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, [quartile, domain, maxApc]);
  useEffect(() => {
    const t = setTimeout(fetchAll, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const sorted = useMemo(() => {
    const list = [...items];
    const { key, dir } = sort;
    const mul = dir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      let av, bv;
      if (key === "quartile") {
        av = QUARTILE_ORDER[a.quartile] || 99;
        bv = QUARTILE_ORDER[b.quartile] || 99;
      } else if (key === "impact_factor" || key === "apc_usd" || key === "review_time_weeks") {
        av = Number(a[key] || 0); bv = Number(b[key] || 0);
      } else if (key === "domain") {
        av = (a.domains?.[0] || "").toLowerCase();
        bv = (b.domains?.[0] || "").toLowerCase();
      } else {
        av = (a[key] || "").toString().toLowerCase();
        bv = (b[key] || "").toString().toLowerCase();
      }
      if (av < bv) return -1 * mul;
      if (av > bv) return 1 * mul;
      return 0;
    });
    return list;
  }, [items, sort]);

  return (
    <div data-testid="journals-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">JOURNALS & CONFERENCES</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">
        Searchable <em className="italic text-kb-purple">publication venues</em>.
      </h1>
      <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
        Browse curated journals with quartile, impact factor, APC, indexing data and submission alerts.
        Click any column header to sort.
      </p>

      <div className="mt-8 grid lg:grid-cols-12 gap-3">
        <div className="lg:col-span-5 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-kb-ink-light" />
          <input
            className="kb-input pl-9"
            placeholder="Search by name, publisher or domain…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="journals-search-input"
          />
        </div>
        <div className="lg:col-span-3">
          <select className="kb-input" value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="journals-domain-select">
            {DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="lg:col-span-2">
          <input
            type="number"
            placeholder="Max APC ($)"
            className="kb-input"
            value={maxApc}
            onChange={(e) => setMaxApc(e.target.value)}
            data-testid="journals-max-apc-input"
          />
        </div>
        <div className="lg:col-span-2 flex gap-1">
          {QUARTILES.map((q2) => (
            <button
              key={q2}
              onClick={() => setQuartile(q2)}
              className={`flex-1 kb-mono text-[10px] uppercase tracking-wider px-2 py-2 rounded-sm border transition-colors ${
                quartile === q2 ? "border-kb-purple bg-kb-purple text-white" : "border-kb-divider bg-white text-kb-ink-muted"
              }`}
              data-testid={`journals-quartile-${q2.toLowerCase()}`}
            >{q2}</button>
          ))}
        </div>
      </div>

      <div className="mt-8 border border-kb-divider rounded-sm bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 lg:px-6 py-3 border-b border-kb-divider bg-kb-subtle kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
          <div className="col-span-5"><SortableHeader label="Journal" sortKey="name" sort={sort} onSort={onSort} /></div>
          <div className="col-span-2"><SortableHeader label="Quartile / IF" sortKey="quartile" sort={sort} onSort={onSort} /></div>
          <div className="col-span-2"><SortableHeader label="Domain" sortKey="domain" sort={sort} onSort={onSort} /></div>
          <div className="col-span-2"><SortableHeader label="APC (USD)" sortKey="apc_usd" sort={sort} onSort={onSort} /></div>
          <div className="col-span-1 text-right">Submit</div>
        </div>
        {loading ? (
          <div className="p-6 kb-mono text-xs text-kb-ink-muted">Loading…</div>
        ) : sorted.length === 0 ? (
          <div className="p-10 text-center text-sm text-kb-ink-muted">No journals match your filters.</div>
        ) : (
          sorted.map((j) => (
            <div key={j.id} className="grid grid-cols-12 px-4 lg:px-6 py-4 border-b border-kb-divider last:border-0 items-center hover:bg-kb-subtle/50 transition-colors" data-testid={`journal-row-${j.id}`}>
              <div className="col-span-12 lg:col-span-5">
                <div className="text-kb-ink font-medium">{j.name}</div>
                <div className="kb-mono text-[10px] text-kb-ink-light mt-0.5">{j.publisher} · Review {j.review_time_weeks}w · Indexed: {(j.indexed_in || []).slice(0,2).join(", ")}</div>
              </div>
              <div className="col-span-6 lg:col-span-2 mt-2 lg:mt-0 flex items-center gap-2">
                <span className={`kb-mono text-[10px] uppercase px-2 py-1 rounded-sm border ${quartileBadge[j.quartile] || ""}`}>{j.quartile}</span>
                <span className="kb-mono text-xs text-kb-ink">IF {j.impact_factor}</span>
              </div>
              <div className="col-span-6 lg:col-span-2 mt-2 lg:mt-0 text-xs text-kb-ink-muted truncate">{(j.domains || []).slice(0,2).join(", ")}</div>
              <div className="col-span-6 lg:col-span-2 mt-2 lg:mt-0 font-mono text-kb-purple">${Number(j.apc_usd).toLocaleString()}</div>
              <div className="col-span-6 lg:col-span-1 mt-2 lg:mt-0 text-right">
                <a href={j.submission_url} target="_blank" rel="noreferrer" className="text-kb-purple inline-flex items-center gap-1.5 text-sm" data-testid={`journal-submit-${j.id}`}>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
